// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Page from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@rpsc/App/Components/Common/XngZone";
import ProductDetailsReplacementAnchor from "@rpsc/App/Components/ProductDetails/ProductDetailsReplacementAnchor";
import ProductDetailsSummary from "@rpsc/App/Components/ProductDetails/ProductDetailsSummary";
import xUseWidgetBadges from "@rpsc/App/Hooks/xUseWidgetBadges";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import ProductImageBadges from "@rpsc/Overrides/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@rpsc/Overrides/Components/ProductPartNumbers";
import * as React from "react";
import { css } from "styled-components";

export interface ProductDetailsViewStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    middleGridItem?: GridItemProps;
    middleGridItemHidden?: HiddenProps;
    bottomGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemHidden?: HiddenProps;
    // XNG-Change
    pageContainer?: InjectableCss;
    bottomSection?: InjectableCss;
    columnsContainer?: InjectableCss;
    twoColumnsSection?: InjectableCss;
    column?: InjectableCss;
    rightColumn?: InjectableCss;
    imageContainer?: InjectableCss;
    specificationsContainer?: InjectableCss;
    generalActionsContainer?: InjectableCss;
    priceActionsContainer?: InjectableCss;
    documentsWrapper?: InjectableCss;
    divider?: InjectableCss;
    infoDescriptionSection?: InjectableCss;
    productNumbersWrapper?: InjectableCss;
    productMyNumbersWrapper?: InjectableCss;
    badgesContainer?: InjectableCss;
    stickyWrapper?: InjectableCss;
    stickyPage?: InjectableCss;
    footerZone?: InjectableCss;
    carouselsContainer?: InjectableCss;
    partNumbersStyles?: ProductPartNumbersStyles;
    brand?: ProductBrandStyles;
    description?: InjectableCss;
    primaryImageZone?: InjectableCss;
}

export const viewStyles: ProductDetailsViewStyles = {
    leftColumnGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            overflow: hidden;
            direction: ltr;
        `,
    },
    topGridItem: { width: 12 },
    middleGridItem: { width: [12, 12, 0, 0, 0] },
    middleGridItemHidden: {
        above: "sm",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    bottomGridItem: { width: 12 },
    rightColumnGridItem: { width: [0, 0, 4, 4, 4] },
    rightColumnGridItemHidden: {
        below: "md",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    container: {
        css: css`
            direction: rtl;
        `,
    },
};

const styles = viewStyles;

// XNG-Change Add shell context

type Props = WidgetProps & HasShellContext & HasProductContext;

// XNG-Change: change structure to match design, Add sticky header
const ProductDetailsView: React.FC<Props> = props => {
    const [stickyVisible, setStickyVisible] = React.useState(false);
    const addToCartRef = React.useRef<HTMLDivElement | null>(null);
    const { isTabletScreen, isMobileScreen } = useAppSelector((state: ApplicationState) => ({
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    }));

    const isShellEditingMood = props.shellContext?.isEditing;

    // XNG-Change
    const { textBadges, imageBadges } = xUseWidgetBadges({
        productContext: props.productContext,
        showImageBadges: true,
        showTextBadges: true,
    });
    const product = props.productContext.product;

    const handleSticky = () => {
        /* we need the height of the header to be excluded as we will overlap this area (add to cart button) */
        const partiallyPdpInfoHeight = (addToCartRef.current?.scrollHeight || 0) + 60;
        if (window.scrollY > partiallyPdpInfoHeight) {
            setStickyVisible(true);
        } else {
            setStickyVisible(false);
        }
    };

    React.useEffect(() => {
        document.addEventListener("scroll", handleSticky, { passive: true });
        handleSticky();
        return () => {
            document.removeEventListener("scroll", handleSticky);
        };
    }, []);

    const RightSideSection = (
        <>
            <StyledWrapper {...styles.priceActionsContainer} ref={addToCartRef}>
                <XngZone contentId={props.id} zoneName="Content4" hint="Print & Price & Actions" />
            </StyledWrapper>
            <ProductDetailsReplacementAnchor />
            <StyledWrapper {...styles.documentsWrapper}>
                <XngZone contentId={props.id} zoneName="Content7" hint="Documents" />
            </StyledWrapper>
        </>
    );

    const RightSideFooterZone = (
        <StyledWrapper {...styles.footerZone}>
            <XngZone contentId={props.id} zoneName="Content10" hint="Content Card" />
        </StyledWrapper>
    );

    const InfoPart = (
        <>
            <XngZone contentId={props.id} zoneName="Content5" hint="Title" />
            <StyledWrapper {...styles.productNumbersWrapper}>
                {product.brand && <ProductBrand brand={product.brand} extendedStyles={styles.brand} />}
                <ProductPartNumbers
                    productNumber={product.productNumber}
                    customerProductNumber={product.customerProductNumber}
                    upcNumber={product.detail?.upcCode}
                    manufacturerItem={product.manufacturerItem}
                    displayUpcNumber
                    extendedStyles={styles.partNumbersStyles}
                />
            </StyledWrapper>
            {(!!textBadges?.length || !!imageBadges?.length) && (
                <StyledWrapper {...styles.badgesContainer}>
                    <ProductTextBadges badgeList={textBadges as any} maxBadges={1} />
                    <ProductImageBadges badgeList={imageBadges as any} maxBadges={4} />
                </StyledWrapper>
            )}
        </>
    );

    return (
        <StyledWrapper {...styles.pageContainer}>
            {isTabletScreen ? (
                <>
                    <StyledWrapper {...styles.infoDescriptionSection}>{InfoPart}</StyledWrapper>
                    <StyledWrapper {...styles.description}>
                        <XngZone contentId={props.id} zoneName="Content9" hint="Description zone" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.column}>
                        <StyledWrapper {...styles.twoColumnsSection}>
                            <XngZone contentId={props.id} zoneName="Content0" hint="Image" />
                            <StyledWrapper {...styles.rightColumn} data-test-selector="productDetails_rightColumn">
                                {RightSideSection}
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.specificationsContainer}>
                            <XngZone contentId={props.id} zoneName="Content1" hint="Zone For Product Specifications" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.carouselsContainer}>
                            <XngZone contentId={props.id} zoneName="Content6" hint="Carousel" />
                        </StyledWrapper>
                        {RightSideFooterZone}

                        <StyledWrapper data-test-selector="productDetails_bottomSection" {...styles.bottomSection}>
                            <XngZone contentId={props.id} zoneName="Content11" hint="Zone For ExtraElements" />
                        </StyledWrapper>
                    </StyledWrapper>
                </>
            ) : (
                <>
                    <StyledWrapper {...styles.columnsContainer}>
                        <StyledWrapper {...styles.column} data-test-selector="productDetails_leftColumn">
                            <StyledWrapper {...styles.twoColumnsSection}>
                                <StyledWrapper {...styles.primaryImageZone}>
                                    <XngZone contentId={props.id} zoneName="Content0" hint="Image" />
                                </StyledWrapper>
                                <StyledWrapper {...styles.infoDescriptionSection}>
                                    {InfoPart}
                                    <XngZone contentId={props.id} zoneName="Content9" hint="Description zone" />
                                </StyledWrapper>
                            </StyledWrapper>

                            <StyledWrapper {...styles.specificationsContainer}>
                                <XngZone
                                    contentId={props.id}
                                    zoneName="Content1"
                                    hint="Zone For Product Specifications"
                                />
                            </StyledWrapper>
                            <StyledWrapper {...styles.carouselsContainer}>
                                <XngZone contentId={props.id} zoneName="Content6" hint="Carousel" />
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.rightColumn} data-test-selector="productDetails_rightColumn">
                            {RightSideSection}
                            {RightSideFooterZone}
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper data-test-selector="productDetails_bottomSection" {...styles.bottomSection}>
                        <XngZone contentId={props.id} zoneName="Content11" hint="Zone For ExtraElements" />
                    </StyledWrapper>
                </>
            )}
            {/* XNG-Changes: dont show sticky header on mobile only  */}
            {!isMobileScreen && (
                <StyledWrapper
                    className={`${stickyVisible ? "stickyVisible" : "stickyHidden"} ${
                        isShellEditingMood ? "editingMode" : ""
                    }`}
                    data-test-selector="productDetails-sticky-header"
                    {...styles.stickyWrapper}
                >
                    <Page background="common.white" {...styles.stickyPage}>
                        <ProductDetailsSummary />
                    </Page>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    // XNG-Change
    component: withIsInShell(withProductContext(ProductDetailsView)),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
